import {
  ActionConfirmationModal,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Separator,
  Skeleton,
  Typography,
} from "@/components/ui";

import { FloatingSidebarToggleButton } from "@/components/ui/shadcn/Sidebar/FloatingSidebarToggleButton";
import { useUser } from "@/components/UserProvider";
import { useDeleteContact } from "@/lib/data/react-query";
import {
  useCancelContactPortalInvitation,
  useContactPortalAccess,
  useResendContactPortalInvitation,
  useRevokeContactPortalAccess,
} from "@/lib/data/react-query/contacts/contact-portal-access";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { formatContactsName } from "@/lib/utils/format";
import { useNavigate } from "@tanstack/react-router";
import { startCase } from "lodash";
import {
  MailWarning,
  Pencil,
  RefreshCw,
  Send,
  Trash,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { isAxiosError } from "axios";
import toast from "react-hot-toast";
import { retrieveContactQueryOptions } from "@/lib/data/react-query/contacts/contact";
import { useSuspenseQuery } from "@tanstack/react-query";
import type { APIError, ContactEventLog } from "@/lib/types";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { EditContactModal } from "../AddOrEditContactModal/EditContactModal";
import ContactAddressesSection from "../ContactAddressesSection/ContactAddressesSection";
import ContactEmailsSection from "../ContactEmailsSection/ContactEmailsSection";
import ContactEmailCommunicationSection from "../ContactEmailCommunicationSection/ContactEmailCommunicationSection";
import ContactFaxesSection from "../ContactFaxesSection/ContactFaxesSection";
import ContactPhoneNumbersSection from "../ContactPhoneNumbersSection/ContactPhoneNumbersSection";
import ContactConnectionsSection from "../ContactConnectionsSection/ContactConnectionsSection";
import ContactMatterConnectionsSection from "../ContactMatterConnectionsSection/ContactMatterConnectionsSection";
import ContactTagConnectionsSection from "../ContactTagConnectionsSection/ContactTagConnectionsSection";
import ContactWebsitesSection from "../ContactWebsitesSection/ContactWebsitesSection";
import { ContactEventLogs } from "../event-logs/ContactEventLogs";
import { ContactPortalInviteDialog } from "../ContactPortalInviteDialog";
import { ContactDetailsSidebar } from "./ContactDetailsSidebar";
import { ContactIndividualDetails } from "./ContactIndividualDetails";
import { ContactOrganizationDetails } from "./ContactOrganizationDetails";

export default function ContactDetails({ contactId }: { contactId: string }) {
  const { data: contactData } = useSuspenseQuery(
    retrieveContactQueryOptions(contactId),
  );
  const contactPayload = contactData.data;
  const isContactPayload =
    !Array.isArray(contactPayload) && Boolean(contactPayload.id);
  const deletedEventLogs = Array.isArray(contactPayload) ? contactPayload : [];
  const portalAccess = useContactPortalAccess(
    contactId,
    !Array.isArray(contactPayload) && contactPayload.type === "individual",
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPortalInviteOpen, setIsPortalInviteOpen] = useState(false);
  const [isRevokingPortalAccess, setIsRevokingPortalAccess] = useState(false);
  const [isCancellingPortalInvitation, setIsCancellingPortalInvitation] =
    useState(false);
  const { hasActionPermission } = useUser();
  const navigate = useNavigate();
  const deleteContact = useDeleteContact();
  const revokePortalAccess = useRevokeContactPortalAccess();
  const cancelPortalInvitation = useCancelContactPortalInvitation();
  const resendPortalInvitation = useResendContactPortalInvitation();
  const canViewNotes = true;
  const canViewEventLog = true;
  const canCreateNotes = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.create,
  );
  const canEditNotes = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDeleteNotes = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canRevokePortalAccess = hasActionPermission(
    ACTION_PERMISSION_KEYS.contactPortalAccess,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canResendPortalInvitation = hasActionPermission(
    ACTION_PERMISSION_KEYS.contactPortalAccess,
    ACTION_PERMISSION_VALUES.update,
  );

  if (!isContactPayload) {
    return (
      <ContactDeletedEventLogView
        eventLogs={deletedEventLogs as ContactEventLog[]}
        canViewEventLog={canViewEventLog}
      />
    );
  }

  const contact = contactPayload;
  const contactSubTypes =
    contact.type === "individual"
      ? contact.individualSubTypes
      : contact.type === "organization"
        ? contact.organizationSubTypes
        : undefined;
  const contactDisplayName = formatContactsName(contact);
  const hasActivePortalAccess =
    portalAccess.data?.data.activeConnection === true;
  const invitationStatus = portalAccess.data?.data.status;
  const invitationExpiresAt = portalAccess.data?.data.expiresAt;
  const invitationExpired =
    Boolean(invitationExpiresAt) &&
    new Date(invitationExpiresAt ?? "").getTime() <= Date.now();
  const hasPendingPortalInvitation =
    !hasActivePortalAccess &&
    invitationStatus === "pending" &&
    !invitationExpired;
  const hasExpiredPortalInvitation =
    !hasActivePortalAccess &&
    (invitationStatus === "expired" ||
      (invitationStatus === "pending" && invitationExpired));
  const pendingInvitationId = hasPendingPortalInvitation
    ? portalAccess.data?.data.invitationId
    : null;
  const resendInvitationId =
    hasPendingPortalInvitation || hasExpiredPortalInvitation
      ? portalAccess.data?.data.invitationId
      : null;
  const portalEmail = portalAccess.data?.data.invitedEmail?.trim();
  const portalEmailMissingFromContact =
    (hasActivePortalAccess || hasPendingPortalInvitation) &&
    portalEmail &&
    !contact.emails.some(
      ({ address }) =>
        address?.trim().toLowerCase() === portalEmail.toLowerCase(),
    );
  const handleDeleteContact = () => {
    deleteContact.mutate({ id: contact.id });
    void navigate({ to: "/contacts" });
  };
  const handleRevokePortalAccess = async () => {
    try {
      await revokePortalAccess.mutateAsync(contact.id);
      toast.success("Portal access revoked.");
    } catch (error) {
      toast.error(
        isAxiosError<APIError>(error)
          ? (error.response?.data.meta.userMsg ??
              "Could not revoke portal access. Please try again.")
          : "Could not revoke portal access. Please try again.",
      );
    }
  };
  const handleCancelPortalInvitation = async () => {
    if (!pendingInvitationId) return;
    try {
      await cancelPortalInvitation.mutateAsync({
        contactId: contact.id,
        invitationId: pendingInvitationId,
      });
      toast.success("Portal invitation canceled.");
    } catch (error) {
      toast.error(
        isAxiosError<APIError>(error)
          ? (error.response?.data.meta.userMsg ??
              "Could not cancel the portal invitation. Please try again.")
          : "Could not cancel the portal invitation. Please try again.",
      );
    }
  };
  const handleResendPortalInvitation = async () => {
    if (!resendInvitationId) return;
    try {
      await resendPortalInvitation.mutateAsync({
        contactId: contact.id,
        invitationId: resendInvitationId,
        idempotencyKey: crypto.randomUUID(),
      });
      toast.success(
        `Portal invitation queued for ${portalEmail ?? "this contact"}.`,
      );
    } catch (error) {
      toast.error(
        isAxiosError<APIError>(error)
          ? (error.response?.data.meta.userMsg ??
              "Could not resend the portal invitation. Please try again.")
          : "Could not resend the portal invitation. Please try again.",
      );
    }
  };

  return (
    <>
      <div className="flex h-full min-h-0 w-full flex-col overflow-hidden lg:flex-row">
        <div className="flex min-h-0 min-w-0 flex-[3] flex-col overflow-hidden">
          <div className="relative flex h-full min-h-0 flex-col overflow-hidden p-4 sm:p-6">
            <div className="absolute top-4 left-4 z-50">
              <FloatingSidebarToggleButton />
            </div>
            <Card className="flex h-full min-h-0 flex-col overflow-hidden">
              <CardContent className="flex h-full min-h-0 flex-col overflow-hidden p-6">
                <div className="flex flex-col gap-4 border-b pb-6 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex flex-col space-y-4">
                    <div className="flex flex-col">
                      <Typography
                        tag="h2"
                        className="text-2xl font-bold"
                        color="primary"
                      >
                        {contactDisplayName || "Contact"}
                      </Typography>
                      <div className="text-foreground mt-2 flex flex-wrap items-center gap-2 text-sm">
                        <Badge className="h-6 text-xs">
                          {startCase(contact.type ?? "")}
                        </Badge>
                        {contactSubTypes?.map((type) => (
                          <Badge
                            variant="secondary"
                            key={type}
                            className="h-6 text-xs"
                          >
                            {startCase(type)}
                          </Badge>
                        ))}
                        {contact.type === "individual" &&
                          contact.individualMinor && (
                            <Badge
                              variant="amber"
                              className="h-6 text-xs text-yellow-500"
                            >
                              Minor
                            </Badge>
                          )}
                        {contact.type === "individual" &&
                          contact.individualDeceased && (
                            <Badge variant="red" className="h-6 text-xs">
                              Deceased
                            </Badge>
                          )}
                      </div>
                      {contact.type === "individual" ? (
                        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                          {portalAccess.isPending ? (
                            <Skeleton
                              role="status"
                              aria-label="Loading portal access"
                              className="h-6 w-32 rounded-full"
                            />
                          ) : portalAccess.isError ? (
                            <Badge variant="muted" className="h-6 text-xs">
                              Portal status unavailable
                            </Badge>
                          ) : hasActivePortalAccess ? (
                            <div className="group/portal relative inline-flex items-center">
                              <TooltipWrapper
                                message={
                                  portalAccess.data.data.invitedEmail ??
                                  "Portal account email unavailable"
                                }
                              >
                                <Badge
                                  variant="blue"
                                  className={
                                    canRevokePortalAccess
                                      ? "h-6 pr-7 text-xs"
                                      : "h-6 text-xs"
                                  }
                                  tabIndex={0}
                                >
                                  Contact portal account active
                                </Badge>
                              </TooltipWrapper>
                              {canRevokePortalAccess ? (
                                <TooltipWrapper message="Revoke portal access">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute top-0 right-0 h-6 w-6 rounded-full text-blue-800 opacity-0 transition-opacity group-hover/portal:opacity-100 hover:bg-red-100 hover:text-red-700 focus-visible:opacity-100 dark:text-blue-300"
                                    aria-label="Revoke portal access"
                                    onClick={() => {
                                      setIsRevokingPortalAccess(true);
                                    }}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipWrapper>
                              ) : null}
                            </div>
                          ) : hasPendingPortalInvitation ||
                            hasExpiredPortalInvitation ? (
                            <div className="group/invite relative inline-flex items-center">
                              <TooltipWrapper
                                message={
                                  portalAccess.data.data.invitedEmail ??
                                  "Invitation email unavailable"
                                }
                              >
                                <Badge
                                  variant="amber"
                                  className={
                                    canResendPortalInvitation &&
                                    resendInvitationId &&
                                    canRevokePortalAccess &&
                                    pendingInvitationId
                                      ? "h-6 pr-13 text-xs"
                                      : (canResendPortalInvitation &&
                                            resendInvitationId) ||
                                          (canRevokePortalAccess &&
                                            pendingInvitationId)
                                        ? "h-6 pr-7 text-xs"
                                        : "h-6 text-xs"
                                  }
                                  tabIndex={0}
                                >
                                  {hasExpiredPortalInvitation
                                    ? "Portal invitation expired"
                                    : "Portal invitation pending"}
                                </Badge>
                              </TooltipWrapper>
                              {canResendPortalInvitation &&
                              resendInvitationId ? (
                                <TooltipWrapper message="Resend portal invitation">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className={`absolute top-0 h-6 w-6 rounded-full text-amber-800 hover:bg-amber-200 dark:text-amber-300 ${canRevokePortalAccess && pendingInvitationId ? "right-6" : "right-0"}`}
                                    aria-label="Resend portal invitation"
                                    disabled={resendPortalInvitation.isPending}
                                    onClick={() => {
                                      void handleResendPortalInvitation();
                                    }}
                                  >
                                    <RefreshCw className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipWrapper>
                              ) : null}
                              {canRevokePortalAccess && pendingInvitationId ? (
                                <TooltipWrapper message="Cancel portal invitation">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute top-0 right-0 h-6 w-6 rounded-full text-amber-800 hover:bg-red-100 hover:text-red-700 dark:text-amber-300"
                                    aria-label="Cancel portal invitation"
                                    onClick={() => {
                                      setIsCancellingPortalInvitation(true);
                                    }}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipWrapper>
                              ) : null}
                            </div>
                          ) : (
                            <Badge variant="muted" className="h-6 text-xs">
                              No portal account
                            </Badge>
                          )}
                          {!portalAccess.isPending &&
                          !portalAccess.isError &&
                          !hasActivePortalAccess &&
                          !hasPendingPortalInvitation &&
                          !hasExpiredPortalInvitation &&
                          hasActionPermission(
                            ACTION_PERMISSION_KEYS.contactPortalAccess,
                            ACTION_PERMISSION_VALUES.create,
                          ) ? (
                            <Button
                              type="button"
                              variant="link"
                              size="xs"
                              className="h-6 px-0 text-xs"
                              onClick={() => {
                                setIsPortalInviteOpen(true);
                              }}
                            >
                              <Send className="h-4 w-4" />
                              Invite to portal
                            </Button>
                          ) : null}
                          {portalEmailMissingFromContact ? (
                            <div
                              role="status"
                              className="flex basis-full items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400"
                            >
                              <MailWarning
                                className="h-4 w-4 shrink-0"
                                aria-hidden="true"
                              />
                              <span>
                                {hasActivePortalAccess
                                  ? "Portal account email"
                                  : "Portal invitation email"}{" "}
                                <span className="font-medium">
                                  {portalEmail}
                                </span>{" "}
                                is not saved on this contact.
                              </span>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {hasActionPermission(
                      ACTION_PERMISSION_KEYS.contact,
                      ACTION_PERMISSION_VALUES.delete,
                    ) && (
                      <TooltipWrapper message="Delete contact">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-destructive border-destructive/30 hover:bg-destructive/5 hover:text-destructive shrink-0"
                          onClick={() => {
                            setIsDeleting(true);
                          }}
                        >
                          <Trash className="h-4 w-4" />
                          Delete contact
                        </Button>
                      </TooltipWrapper>
                    )}
                  </div>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto pr-4">
                  <div className="space-y-6 py-6">
                    <>
                      <div className="relative space-y-2 pr-8 text-sm text-gray-500 dark:text-gray-400">
                        {hasActionPermission(
                          ACTION_PERMISSION_KEYS.contact,
                          ACTION_PERMISSION_VALUES.update,
                        ) && (
                          <div className="absolute top-0 right-0">
                            <TooltipWrapper message="Edit contact details">
                              <span>
                                <Button
                                  data-testid="edit-contact-details"
                                  variant="ghost"
                                  size="sm"
                                  aria-label="Edit contact details"
                                  onClick={() => {
                                    setIsEditing(true);
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </span>
                            </TooltipWrapper>
                          </div>
                        )}
                        {contact.type === "individual" && (
                          <ContactIndividualDetails contact={contact} />
                        )}
                        {contact.type === "organization" && (
                          <ContactOrganizationDetails contact={contact} />
                        )}
                        {contact.type === "group" && (
                          <table className="table table-auto">
                            <tbody>
                              <tr className="table-row">
                                <td>Group description:</td>
                                <td className="pl-2">
                                  {contact.groupDescription || "-"}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        )}
                      </div>
                      <Separator />
                    </>

                    <>
                      <ContactEmailsSection
                        emails={contact.emails}
                        contactId={contact.id}
                        hasCreatePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactEmail,
                          ACTION_PERMISSION_VALUES.create,
                        )}
                        hasDeletePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactEmail,
                          ACTION_PERMISSION_VALUES.delete,
                        )}
                        hasEditPermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactEmail,
                          ACTION_PERMISSION_VALUES.update,
                        )}
                        organizationSubTypes={
                          contact.organizationSubTypes ?? undefined
                        }
                        type={contact.type ?? undefined}
                      />
                    </>

                    <>
                      <Separator />
                      <ContactAddressesSection
                        addresses={contact.addresses}
                        contactId={contact.id}
                        hasCreatePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactAddress,
                          ACTION_PERMISSION_VALUES.create,
                        )}
                        hasDeletePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactAddress,
                          ACTION_PERMISSION_VALUES.delete,
                        )}
                        hasEditPermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactAddress,
                          ACTION_PERMISSION_VALUES.update,
                        )}
                      />
                    </>

                    <>
                      <Separator />
                      <ContactPhoneNumbersSection
                        phoneNumbers={contact.phones}
                        contactId={contact.id}
                        hasCreatePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactPhone,
                          ACTION_PERMISSION_VALUES.create,
                        )}
                        hasDeletePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactPhone,
                          ACTION_PERMISSION_VALUES.delete,
                        )}
                        hasEditPermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactPhone,
                          ACTION_PERMISSION_VALUES.update,
                        )}
                        organizationSubTypes={
                          contact.organizationSubTypes ?? undefined
                        }
                        type={contact.type ?? undefined}
                      />
                    </>

                    <>
                      <Separator />
                      <ContactFaxesSection
                        faxes={contact.faxes}
                        contactId={contact.id}
                        hasCreatePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactFax,
                          ACTION_PERMISSION_VALUES.create,
                        )}
                        hasDeletePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactFax,
                          ACTION_PERMISSION_VALUES.delete,
                        )}
                        hasEditPermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactFax,
                          ACTION_PERMISSION_VALUES.update,
                        )}
                      />
                    </>

                    <>
                      <Separator />
                      <ContactWebsitesSection
                        websites={contact.websites}
                        contactId={contact.id}
                        hasCreatePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactWebsite,
                          ACTION_PERMISSION_VALUES.create,
                        )}
                        hasDeletePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactWebsite,
                          ACTION_PERMISSION_VALUES.delete,
                        )}
                        hasEditPermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactWebsite,
                          ACTION_PERMISSION_VALUES.update,
                        )}
                      />
                    </>

                    <>
                      <Separator />
                      <ContactConnectionsSection
                        contact={contact}
                        hasCreatePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactConnection,
                          ACTION_PERMISSION_VALUES.create,
                        )}
                        hasDeletePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactConnection,
                          ACTION_PERMISSION_VALUES.delete,
                        )}
                      />
                    </>

                    <Separator />
                    <ContactMatterConnectionsSection contactId={contactId} />

                    <>
                      <Separator />
                      <ContactEmailCommunicationSection
                        communications={contact.communicationEmails}
                      />
                    </>

                    <>
                      <Separator />
                      <ContactTagConnectionsSection
                        tagConnections={contact.tagConnections}
                        contactId={contact.id}
                        hasCreatePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactTagConnection,
                          ACTION_PERMISSION_VALUES.create,
                        )}
                        hasDeletePermission={hasActionPermission(
                          ACTION_PERMISSION_KEYS.contactTagConnection,
                          ACTION_PERMISSION_VALUES.delete,
                        )}
                      />
                    </>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <aside className="border-border bg-muted flex min-h-0 flex-[2] flex-col overflow-hidden border-t lg:h-full lg:w-[400px] lg:flex-none lg:border-t-0 lg:border-l">
          <ContactDetailsSidebar
            contact={contact}
            canViewNotes={canViewNotes}
            canCreateNotes={canCreateNotes}
            canEditNotes={canEditNotes}
            canDeleteNotes={canDeleteNotes}
            canViewEventLog={canViewEventLog}
          />
        </aside>
      </div>
      <ActionConfirmationModal
        open={isDeleting}
        setOpen={setIsDeleting}
        title="Are you sure you want to delete this contact?"
        afterActionFn={handleDeleteContact}
      />
      <ActionConfirmationModal
        open={isRevokingPortalAccess}
        setOpen={setIsRevokingPortalAccess}
        title="Revoke portal access?"
        description="This removes this contact's access to this firm's portal requests and surveys. Their portal account and contact emails will remain."
        buttonText="Revoke access"
        isActionPending={revokePortalAccess.isPending}
        afterActionFn={handleRevokePortalAccess}
      />
      <ActionConfirmationModal
        open={isCancellingPortalInvitation}
        setOpen={setIsCancellingPortalInvitation}
        title="Cancel portal invitation?"
        description={`The invitation link sent to ${portalEmail ?? "this contact"} will stop working. This does not delete the contact email or any portal account.`}
        buttonText="Cancel invitation"
        cancelText="Keep invitation"
        isActionPending={cancelPortalInvitation.isPending}
        afterActionFn={handleCancelPortalInvitation}
      />
      <EditContactModal
        hasEditPermission={hasActionPermission(
          ACTION_PERMISSION_KEYS.contact,
          ACTION_PERMISSION_VALUES.update,
        )}
        open={isEditing}
        setOpen={setIsEditing}
        activeContact={contact}
        hideSubItems
      />
      {isPortalInviteOpen && (
        <ContactPortalInviteDialog
          contactId={contact.id}
          open={isPortalInviteOpen}
          onOpenChange={setIsPortalInviteOpen}
        />
      )}
    </>
  );
}

const ContactDeletedEventLogView = ({
  eventLogs,
  canViewEventLog,
}: {
  eventLogs: ContactEventLog[];
  canViewEventLog: boolean;
}) => {
  return (
    <div className="flex min-h-screen w-full flex-col gap-4 p-4 sm:p-6">
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader className="space-y-1">
          <Typography variant="subHeading">This contact was deleted</Typography>
          <Typography className="text-muted-foreground text-sm">
            The contact no longer exists, but its event log is still available.
          </Typography>
        </CardHeader>
      </Card>
      <div className="min-h-0 flex-1">
        {canViewEventLog ? (
          <ContactEventLogs events={eventLogs} />
        ) : (
          <Card className="flex h-full items-center justify-center rounded-2xl p-4 text-center">
            <Typography variant="subHeading" className="text-muted-foreground">
              You do not have permission to view contact event logs.
            </Typography>
          </Card>
        )}
      </div>
    </div>
  );
};
